import React from 'react';

import { DatePicker, Row, Tag, Timeline, Typography } from 'antd';
import dayjs from 'dayjs';
import { User } from 'models/user';
import { diffDates } from 'utils/helpers/dates';

import { getTextUnit } from './helpers/formatExperience';
import styles from './TimeLineComp.module.css';

const { Text } = Typography;

enum DateUnit {
  Year,
  Month,
}

const formatExperience = (start: Date, end: Date) => {
  const { years, months } = diffDates(start, end);
  if (years < 1 && months < 1) {
    return `Меньше 1 месяца`;
  }
  if (years < 1) {
    return `${months} ${getTextUnit(months, DateUnit.Month)}`;
  }
  return `${years} ${getTextUnit(years, DateUnit.Year)} ${months > 0 ? months : ''} ${getTextUnit(months, DateUnit.Month)}`;
};

interface ITimeLineBase {
  userData: User;
}

interface ITimeLineView extends ITimeLineBase {
  mode: 'view';
  onChangeExperience?: never;
}

interface ITimeLineEdit extends ITimeLineBase {
  mode: 'edit';
  onChangeExperience?: (index: number, field: 'start' | 'end', value: dayjs.Dayjs | null) => void;
}

type ITimeLineComp = ITimeLineView | ITimeLineEdit;

const TimeLineComp = ({ userData, mode = 'view', onChangeExperience }: ITimeLineComp) => {
  const firstJob = userData.experience[0];
  const othersJob = userData.experience.slice(1);
  const firstJobStart = firstJob ? new Date(firstJob?.start!) : null;
  const firstJobEnd = firstJob ? new Date(firstJob?.end!) : null;

  const items = [
    {
      color: 'green',
      style: { padding: '0 0 20px 0', margin: '0' },
      children: (
        <Tag color="green">
          <Text style={{ color: '#389E0D', fontSize: '12px' }} strong>
            Начало работы
          </Text>
          <Row style={{ fontSize: '12px' }}>
            {mode === 'view' ? (
              `${firstJobStart?.toLocaleDateString()}г.`
            ) : (
              <DatePicker
                size={'small'}
                defaultValue={dayjs(firstJobStart)}
                variant={'borderless'}
                className={styles.customDatepicker}
                allowClear={false}
                format={'DD.MM.YYYY'}
                onChange={(value) => onChangeExperience?.(0, 'start', value)}
              />
            )}
          </Row>
        </Tag>
      ),
    },
  ];

  if (firstJobEnd !== null) {
    items.push({
      color: 'red',
      style: { padding: '0 0 20px 0', margin: '0' },
      children: (
        <>
          <Tag color="red">
            <Text style={{ color: '#CF1322', fontSize: '12px' }} strong>
              Конец работы
            </Text>
            <Row style={{ fontSize: '12px' }}>
              {mode === 'view' ? (
                `${firstJobEnd?.toLocaleDateString()}г.`
              ) : (
                <DatePicker
                  size={'small'}
                  defaultValue={dayjs(firstJobEnd)}
                  variant={'borderless'}
                  className={styles.customDatepicker}
                  allowClear={false}
                  format={'DD.MM.YYYY'}
                  onChange={(value) => onChangeExperience?.(0, 'end', value)}
                />
              )}
            </Row>
          </Tag>
          <Tag
            color="purple"
            style={{ position: 'absolute', top: '-40px', left: '-158px', minWidth: '115px' }}
          >
            <Text style={{ color: '#531DAB', fontSize: '12px' }} strong>
              Стаж
            </Text>
            <Row style={{ fontSize: '12px' }}>
              {firstJobStart !== null && formatExperience(firstJobStart, firstJobEnd)}
            </Row>
          </Tag>
        </>
      ),
    });
  }

  if (othersJob) {
    othersJob.forEach((job, index) => {
      const jobStart = new Date(job.start);
      const jobEnd = new Date(job?.end!);
      items.push({
        color: 'blue',
        style: { padding: '0 0 20px 0', margin: '0' },
        children: (
          <Tag color="blue">
            <Text style={{ color: '#096DD9', fontSize: '12px' }} strong>
              Возобновление работы
            </Text>
            <Row style={{ fontSize: '12px' }}>
              {mode === 'view' ? (
                `${jobStart?.toLocaleDateString()}г.`
              ) : (
                <DatePicker
                  size={'small'}
                  defaultValue={dayjs(jobStart)}
                  variant={'borderless'}
                  className={styles.customDatepicker}
                  allowClear={false}
                  format={'DD.MM.YYYY'}
                  onChange={(value) => onChangeExperience?.(index + 1, 'start', value)}
                />
              )}
            </Row>
          </Tag>
        ),
      });
      if (job.end !== null) {
        items.push({
          color: 'red',
          style: { padding: '0 0 20px 0', margin: '0' },
          children: (
            <>
              <Tag color="red">
                <Text style={{ color: '#CF1322', fontSize: '12px' }} strong>
                  Конец работы
                </Text>
                <Row style={{ fontSize: '12px' }}>
                  {mode === 'view' ? (
                    `${jobEnd?.toLocaleDateString()}г.`
                  ) : (
                    <DatePicker
                      size={'small'}
                      defaultValue={dayjs(jobEnd)}
                      variant={'borderless'}
                      className={styles.customDatepicker}
                      allowClear={false}
                      format={'DD.MM.YYYY'}
                      onChange={(value) => onChangeExperience?.(index + 1, 'end', value)}
                    />
                  )}
                </Row>
              </Tag>
              <Tag
                color="purple"
                style={{ position: 'absolute', top: '-40px', left: '-158px', minWidth: '115px' }}
              >
                <Text style={{ color: '#531DAB', fontSize: '12px' }} strong>
                  Стаж
                </Text>
                <Row style={{ fontSize: '12px' }}>{formatExperience(jobStart, jobEnd)}</Row>
              </Tag>
            </>
          ),
        });
      }
    });
  }
  return (
    <div className={styles.timeline}>
      <Timeline items={items} />
    </div>
  );
};

export default TimeLineComp;
